import React, { useEffect, useState } from 'react';
import Script from 'next/script';
import { adConfig } from '@/config/ads';
import { Card } from '@/components/ui/card';

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

interface AdComponentProps {
  slot?: string;
  format?: 'auto' | 'fluid';
  responsive?: boolean;
  style?: React.CSSProperties;
}

export function AdComponent({
  slot = adConfig?.web?.feedAdSlot,
  format = 'auto',
  responsive = true,
  style
}: AdComponentProps) {
  const [adLoaded, setAdLoaded] = useState(false);
  const [adError, setAdError] = useState(false);
  const [adAttempted, setAdAttempted] = useState(false);

  useEffect(() => {
    // Only attempt to load ads in production
    if (process?.env?.NODE_ENV !== 'production') {
      return;
    }

    // Set a timeout to prevent infinite waiting for ads
    const timeoutId = setTimeout(() => {
      if (!adLoaded && !adError) {
        console.log('Ad loading timed out');
        setAdError(true);
      }
    }, 3000); // 3 second timeout

    try {
      if (!adAttempted) {
        setAdAttempted(true);
        (window.adsbygoogle = window.adsbygoogle || []).push({
          google_ad_client: adConfig?.web?.clientId,
          enable_page_level_ads: true,
          overlays: {bottom: true},
          onload: () => {
            setAdLoaded(true);
            clearTimeout(timeoutId);
          },
          onerror: () => {
            setAdError(true);
            clearTimeout(timeoutId);
          }
        });
      }
    } catch (err) {
      console.error('Error loading ad:', err);
      setAdError(true);
      clearTimeout(timeoutId);
    }

    return () => clearTimeout(timeoutId);
  }, [adLoaded, adError, adAttempted]);

  // If ad fails to load, render a placeholder instead
  if (adError || process?.env?.NODE_ENV !== 'production') {
    return (
      <Card className="relative p-4 text-center bg-accent/10">
        <div className="text-xs text-muted-foreground mb-2">Sponsored</div>
        <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p className="text-muted-foreground">Advertisement</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Script
        id="adsense-script"
        async
        src="https://pagead2?.googlesyndication?.com/pagead/js/adsbygoogle.js"
        strategy="afterInteractive"
        onError={() => setAdError(true)}
        crossOrigin="anonymous"
      />
      <div style={{ textAlign: 'center', overflow: 'hidden', ...style }}>
        <ins
          className="adsbygoogle"
          style={{
            display: 'block',
            textAlign: 'center',
            ...style,
          }}
          data-ad-client={adConfig?.web?.clientId}
          data-ad-slot={slot}
          data-ad-format={format}
          data-full-width-responsive={responsive}
        />
      </div>
    </>
  );
}